import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import {
  Box,
  Button,
  Input,
  Select,
  Text,
  Spinner,
  Checkbox,
  IconButton,
  Flex,
  WrapItem,
  Wrap,
  FormLabel,
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionButton,
  AccordionIcon,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import Card from "components/card/Card";
import { motion } from "framer-motion";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";
import { getAvailablePages, allPagesAssigned } from "utils/rolePermissions";

const ROLES_PAGE_ID = PAGE_IDS.ROLES;
const PAGES_PAGE_ID = PAGE_IDS.PAGES;

const emptyPermission = () => ({
  pageId: "",
  pageAccess: false,
  readAllAccess: false,
  readOneAccess: false,
  insertAccess: false,
  updateAccess: false,
  deleteAccess: false,
  exportAccess: false,
});

// Renders inside a Modal whose header/footer are owned by ListRole.js — the
// Submit button lives in the modal's ModalFooter (pinned, no scrolling
// needed to reach it) rather than at the end of this form, so this
// component exposes submit() via ref instead of rendering its own submit
// button, and reports isSubmitting up so the external button can show a
// loading state.
const InsertRole = ({ onCreated, onSubmittingChange }, ref) => {
  const { t } = useTranslation();
  const formRef = useRef(null);
  const [roleName, setRoleName] = useState("");
  const [permissions, setPermissions] = useState([emptyPermission()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [pageOptions, setPageOptions] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);

  useImperativeHandle(ref, () => ({
    submit: () => formRef.current?.requestSubmit(),
  }));

  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only isSubmitting should retrigger this, onSubmittingChange is expected to be a fresh inline setter each render
  }, [isSubmitting]);

  // Fetch page options from API
  useEffect(() => {
    const fetchPages = async () => {
      setLoadingPages(true);
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_NEWS_API_URL}/page?page_id=${PAGES_PAGE_ID}`
        );
        if (response && Array.isArray(response.data)) {
          setPageOptions(response.data);
        } else {
          setError(t("Failed to fetch pages"));
        }
      } catch (err) {
        setError(t("Failed to fetch pages"));
      } finally {
        setLoadingPages(false);
      }
    };
    fetchPages();
  }, [t]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const data = {
        page_id: ROLES_PAGE_ID,
        roleName,
        permissions: permissions.map((perm) => ({
          pageId: Number(perm.pageId),
          pageAccess: perm.pageAccess,
          readAllAccess: perm.readAllAccess,
          readOneAccess: perm.readOneAccess,
          insertAccess: perm.insertAccess,
          updateAccess: perm.updateAccess,
          deleteAccess: perm.deleteAccess,
          exportAccess: perm.exportAccess,
        })),
      };

      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/role`,
        "POST",
        data
      );
      // fetchWithToken already throws on a non-2xx response, so getting here
      // means the request succeeded — don't also gate on a `status: "success"`
      // field, since the actual response body doesn't reliably have one.
      if (!response) {
        throw new Error("Failed to create role");
      }
      setError("");
      setSuccess(true);
      // Reset the form and let the parent list refresh itself (via
      // onCreated) instead of doing a full page reload.
      setRoleName("");
      setPermissions([emptyPermission()]);
      onCreated?.();
    } catch (err) {
      setError(err.message || "An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePermissionChange = (index, field, value) => {
    const updatedPermissions = [...permissions];
    updatedPermissions[index][field] = value;
    setPermissions(updatedPermissions);
  };

  const addPermission = () => {
    setPermissions([...permissions, emptyPermission()]);
  };

  const removePermission = (index) => {
    const updatedPermissions = permissions.filter((_, i) => i !== index);
    setPermissions(updatedPermissions);
  };

  const handleSelectAllForPermission = (index, value) => {
    setPermissions((prevPermissions) =>
      prevPermissions.map((perm, i) =>
        i === index
          ? {
              ...perm,
              pageAccess: value,
              readAllAccess: value,
              readOneAccess: value,
              insertAccess: value,
              updateAccess: value,
              deleteAccess: value,
              exportAccess: value,
            }
          : perm
      )
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.5 }}
    >
      <Card p={{ base: "20px", md: "30px" }}>
        <form ref={formRef} onSubmit={handleSubmit}>
          {error && (
            <Text color="red.500" textAlign="center" fontSize="lg">
              {error}
            </Text>
          )}
          {success && (
            <Text color="green.500" textAlign="center">
              {t("Role created successfully!")}
            </Text>
          )}
          <Box>
            <Text fontSize="sm" color="gray.800" mb="2">
              {t("Role Name")}
            </Text>
            <Input
              type="text"
              bg="white"
              borderColor={GOLD_BORDER}
              focusBorderColor={GOLD_BORDER}
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              maxLength={255}
              required
            />
          </Box>

          <Box mt="6">
            <Text fontSize="lg" fontWeight="bold" mb="2">
              {t("Permissions")}
            </Text>

            {/* Accordion for Permissions */}
            <Accordion allowToggle>
              {permissions.map((perm, index) => (
                <AccordionItem key={index} borderWidth="1px" borderRadius="lg" mb={4}>
                  <h2>
                    <AccordionButton _expanded={{ bg: "gray.100" }}>
                      <Box flex="1" textAlign="left">
                        {t("Page")}: {pageOptions.find(p => p.id === parseInt(perm.pageId))?.pageName || t("Select a Page")}
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <Box>
                      {/* Page Selection */}
                      <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                        {t("Page")}
                      </FormLabel>
                      <Select
                        variant="filled"
                        value={perm.pageId}
                        onChange={(e) =>
                          handlePermissionChange(index, "pageId", e.target.value)
                        }
                        required
                        isDisabled={loadingPages}
                      >
                        <option value="">{loadingPages ? t("Loading...") : t("Select a Page")}</option>
                        {getAvailablePages(pageOptions, permissions, index).map((page) => (
                          <option key={page.id} value={page.id}>
                            {t(page.pageName)}
                          </option>
                        ))}
                      </Select>
                    </Box>

                    {/* Select All / Deselect All for Individual Permissions */}
                    <Flex mt={4} gap={2}>
                      <Button
                        type="button"
                        variant="outline"
                        borderColor={GOLD_BORDER}
                        color={GOLD_TEXT}
                        _hover={{ bg: GOLD_LIGHT }}
                        size="xs"
                        onClick={() =>
                          handleSelectAllForPermission(index, true)
                        }
                      >
                        {t("Select All")}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        borderColor="gray.200"
                        color="gray.700"
                        size="xs"
                        onClick={() =>
                          handleSelectAllForPermission(index, false)
                        }
                      >
                        {t("Deselect All")}
                      </Button>
                    </Flex>

                    {/* Permissions Checkboxes */}
                    <Box mt={4}>
                      <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                        {t("Access Rights")}
                      </FormLabel>
                      <Wrap spacing={4}>
                        {[
                          "pageAccess",
                          "readAllAccess",
                          "readOneAccess",
                          "insertAccess",
                          "updateAccess",
                          "deleteAccess",
                          "exportAccess",
                        ].map((field) => (
                          <WrapItem key={field}>
                            <Checkbox
                              colorScheme="brand"
                              isChecked={perm[field]}
                              onChange={(e) =>
                                handlePermissionChange(index, field, e.target.checked)
                              }
                            >
                              <Text fontSize="sm">
                                {t(field.replace(/([A-Z])/g, " $1").toUpperCase())}
                              </Text>
                            </Checkbox>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Box>

                    {/* Delete Button */}
                    <Flex justify="flex-end" mt={4}>
                      <IconButton
                        type="button"
                        aria-label="Remove permission"
                        icon={<DeleteIcon />}
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        onClick={() => removePermission(index)}
                      />
                    </Flex>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Add Permission Button */}
            <Button
              type="button"
              leftIcon={<AddIcon />}
              bg={GOLD_BORDER}
              color="#1A1A1A"
              _hover={{ bg: GOLD_TEXT, color: "white" }}
              _active={{ bg: GOLD_TEXT, color: "white" }}
              size="sm"
              mt={4}
              isDisabled={allPagesAssigned(pageOptions, permissions)}
              onClick={addPermission}
            >
              {t("Add Permission")}
            </Button>
            {allPagesAssigned(pageOptions, permissions) && (
              <Text fontSize="xs" color="gray.500" mt={2}>
                {t("Every page already has a permission row.")}
              </Text>
            )}
          </Box>
        </form>
      </Card>
    </motion.div>
  );
};

export default forwardRef(InsertRole);
